import { useState } from 'react';
import { useAppStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, RotateCw, Check, X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

export default function FlashcardsPage() {
  const { flashcards, activeSubjectId, toggleFlashcardMastery } = useAppStore();
  const subjectCards = flashcards.filter(f => f.subjectId === activeSubjectId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unmastered' | 'mastered'>('all');

  const filteredCards = filter === 'all' ? subjectCards : 
    filter === 'mastered' ? subjectCards.filter(f => f.mastered) : subjectCards.filter(f => !f.mastered);
  
  const current = filteredCards[currentIndex];
  const masteredCount = subjectCards.filter(f => f.mastered).length;

  const goNext = () => { setFlipped(false); setCurrentIndex(i => Math.min(i + 1, filteredCards.length - 1)); };
  const goPrev = () => { setFlipped(false); setCurrentIndex(i => Math.max(i - 1, 0)); };

  return (
    <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">闪读卡</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">关键术语速记，翻转查看定义与示例</p>
      </motion.div>

      {/* Progress bar */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs text-stone-400 font-body">掌握进度</span>
          <span className="text-xs font-semibold text-stone-950 tabular-nums font-body">{masteredCount}/{subjectCards.length}</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, var(--sage-dark), var(--sage))' }}
            animate={{ width: `${(masteredCount / subjectCards.length) * 100}%` }}
          />
        </div>
        <div className="flex gap-2 mt-4">
          {(['all', 'unmastered', 'mastered'] as const).map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setCurrentIndex(0); setFlipped(false); }}
              className={`text-[10px] px-3.5 py-1.5 rounded-xl border transition-all font-body ${
                filter === f ? 'bg-stone-950 text-white border-stone-950' : 'border-stone-200 hover:border-stone-300 text-stone-500'
              }`}
            >
              {f === 'all' ? '全部' : f === 'mastered' ? '已掌握' : '未掌握'} ({
                f === 'all' ? subjectCards.length : f === 'mastered' ? masteredCount : subjectCards.length - masteredCount
              })
            </button>
          ))}
        </div>
      </motion.div>

      {/* Flashcard */}
      {current ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-col items-center">
          <div
            onClick={() => setFlipped(!flipped)}
            className={`w-full max-w-lg cursor-pointer ${flipped ? 'flashcard-flipped' : ''}`}
            style={{ perspective: '1000px', margin: '0 auto' }}
          >
            <div className="flashcard-inner relative" style={{ minHeight: 280 }}>
              {/* Front */}
              <div className="flashcard-front absolute inset-0 gradient-warm rounded-3xl p-6 sm:p-8 flex flex-col items-center justify-center text-center shadow-warm-lg noise-overlay">
                <div className="relative z-10">
                  <Sparkles className="w-6 h-6 text-bronze mb-4 mx-auto" />
                  <h2 className="text-xl font-display text-white leading-relaxed">{current.term}</h2>
                  <p className="text-[10px] text-white/30 mt-5 font-body">点击翻转查看定义</p>
                </div>
              </div>
              {/* Back */}
              <div className="flashcard-back absolute inset-0 bg-white rounded-3xl p-6 sm:p-8 flex flex-col items-center justify-center text-center shadow-warm-lg border border-stone-100">
                <p className="text-sm text-stone-950 leading-relaxed mb-4 font-body">{current.definition}</p>
                {current.example && (
                  <div className="bg-steel-50 rounded-xl px-4 py-2.5 mt-2">
                    <p className="text-[10px] text-steel font-medium font-body">例子</p>
                    <p className="text-xs text-stone-600 font-body">{current.example}</p>
                  </div>
                )}
                <p className="text-[10px] text-stone-300 mt-5 font-body">点击翻转回正面</p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3 sm:gap-4 mt-6 sm:mt-8">
            <button onClick={goPrev} disabled={currentIndex === 0}
              className="p-3 rounded-xl border border-stone-200 hover:bg-linen disabled:opacity-30 transition-all">
              <ChevronLeft className="w-5 h-5 text-stone-600" />
            </button>

            <button
              onClick={() => toggleFlashcardMastery(current.id)}
              className={`px-7 py-3 rounded-xl text-sm font-medium transition-all font-body ${
                current.mastered
                  ? 'bg-sage-50 text-sage-dark hover:bg-sage-light/30 border border-sage-light/30'
                  : 'btn-primary'
              }`}
            >
              {current.mastered ? (
                <span className="flex items-center gap-2"><Check className="w-4 h-4" /> 已掌握</span>
              ) : (
                <span className="flex items-center gap-2"><Check className="w-4 h-4" /> 标记掌握</span>
              )}
            </button>

            <button onClick={goNext} disabled={currentIndex >= filteredCards.length - 1}
              className="p-3 rounded-xl border border-stone-200 hover:bg-linen disabled:opacity-30 transition-all">
              <ChevronRight className="w-5 h-5 text-stone-600" />
            </button>
          </div>

          <p className="text-[10px] text-stone-400 mt-3 tabular-nums font-body">
            {currentIndex + 1} / {filteredCards.length}
          </p>
        </motion.div>
      ) : (
        <div className="text-center py-20">
          <Layers className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-400 font-body">该筛选条件下没有闪读卡</p>
        </div>
      )}

      {/* All cards grid */}
      <div>
        <h3 className="text-base font-display text-stone-950 mb-4">所有术语卡片</h3>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          {subjectCards.map((card, i) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.03 }}
              onClick={() => { setCurrentIndex(filteredCards.findIndex(f => f.id === card.id)); setFlipped(false); }}
              className={`p-4 rounded-2xl border cursor-pointer card-hover ${
                card.mastered ? 'bg-sage-50 border-sage-light/30' : 'bg-white border-stone-100'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-stone-950 font-body">{card.term.split(' ')[0]}</span>
                {card.mastered && <Check className="w-3 h-3 text-sage" />}
              </div>
              <p className="text-[10px] text-stone-400 line-clamp-2 font-body">{card.definition}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
